import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ListBaseComponent } from 'app/shared/components/base/list-base/list-base.component';
import { Constants } from 'app/shared/constants/constants';
import { Permissions } from 'app/shared/constants/permissions';
import { Sections } from 'app/shared/constants/sections';
import { FinancialTransactionFilter } from 'app/shared/filters/financial-transaction.filter';
import { CompanyBase } from 'app/shared/models/company-base.model';
import { CompanyCredit } from 'app/shared/models/company-credit.model';
import { FinancialTransaction } from 'app/shared/models/financial-transaction.model';
import { ModuleBase } from 'app/shared/models/module-base.model';
import { UserBase } from 'app/shared/models/user-base.model';
import { CompanyService } from 'app/shared/services/company.service';
import { SectionService } from 'app/shared/services/section.service';
import { PaginationService } from 'ngx-pagination';
import { format } from 'util';
import { FinancialTransactionService } from '../../../shared/services/financial-transaction.service';
import {
  FinancialTransactionsExportComponent
} from '../financial-transactions-export/financial-transactions-export.component';

@Component({
  selector: 'app-financial-transactions-list',
  templateUrl: './financial-transactions-list.component.html',
  styleUrls: ['./financial-transactions-list.component.scss'],
  providers: [PaginationService],
  encapsulation: ViewEncapsulation.None
})
export class FinancialTransactionsListComponent extends ListBaseComponent implements OnInit {

  fieldCollection = [
    {
      name: 'FinancialTransactionId',
      displayName: 'financialTransactions.financialTransactionIdFieldLabel',
      canSort: true,
      displayClass: "d-none d-lg-table-cell"
    },
    {
      name: 'CreatedAt',
      displayName: 'financialTransactions.createdAtFieldLabel',
      canSort: true,
      displayClass: "d-none d-sm-table-cell"
    },
    {
      name: 'Company.Name',
      displayName: 'financialTransactions.companyNameFieldLabel',
      canSort: false,
      displayClass: "d-none d-sm-table-cell"
    },
    {
      name: 'User.FullName',
      displayName: 'financialTransactions.userFullNameFieldLabel',
      canSort: false,
      displayClass: "d-none d-lg-table-cell"
    },
    {name: 'Module.Name', displayName: 'financialTransactions.moduleNameFieldLabel', canSort: false, displayClass: ""},
    {name: 'Amount', displayName: 'financialTransactions.amountFieldLabel', canSort: true, displayClass: ""},
    {
      name: 'CreditAmountAfter',
      displayName: 'financialTransactions.creditAmountAfterFieldLabel',
      canSort: true,
      displayClass: "d-none d-lg-table-cell"
    }
  ];

  financialTransactionFilter: FinancialTransactionFilter;
  companyId: number;
  companyName: string;
  companyCreditAmount: string;
  translatedTitle: string;
  translatedSuffixTitle: string;

  get title(): string {
    if (this.companyId || this.companyName) {
      return this.translatedTitle + format(this.translatedSuffixTitle, this.companyName)
    }

    return this.translatedTitle;
  }

  get companySelectionListClass(): string {
    if (this.companySelectionListActive) {
      return "selection-list";
    }

    return "";
  }

  get companySelectionListActive(): boolean {
    return (this.hasSelectCompanyPermission && !this.companyId);
  }

  get hasSelectCompanyPermission(): boolean {
    return this.hasPermission(Permissions.FinancialTransactions_SelectCompany);
  }

  constructor(protected sectionService: SectionService, protected financialTransactionService: FinancialTransactionService, protected companyService: CompanyService,
              protected modalService: NgbModal, private route: ActivatedRoute, private router: Router, protected translateService: TranslateService) {
    super(sectionService, Sections.FinancialTransactions, modalService);
  }

  ngOnInit(): void {
    this.route
      .queryParams
      .subscribe(params => {
        this.companyId = params['companyId'];
        this.companyName = params['companyName'];
        this.companyCreditAmount = params['companyCreditAmount'];
      });

    if (!this.companyId) {
      this.companyService.getObject<CompanyCredit>('/company/creditinfo', null, (companyCredit) => {
        if (companyCredit) {
          this.companyId = companyCredit.CompanyId;
          this.companyName = companyCredit.Name;
          this.companyCreditAmount = companyCredit.CreditAmountCurrency;
        }
      });
    }

    this.translateService.get(['financialTransactions.title', 'financialTransactions.suffixTitle'])
      .subscribe(translations => {
        this.translatedTitle = translations['financialTransactions.title'];
        this.translatedSuffixTitle = translations['financialTransactions.suffixTitle'];
      });

    this.financialTransactionFilter = new FinancialTransactionFilter(this.companyId);
    super.initializeView(Constants.FinancialTransactions_List_DefaultSortField, Constants.FinancialTransactions_List_DefaultSortDirectionAscending);
  }

  handleCompanyChanged(company: CompanyBase): void {
    if (company) {
      this.financialTransactionFilter.CompanyId = company.CompanyId;
    } else {
      this.financialTransactionFilter.CompanyId = undefined;
    }

    this.financialTransactionFilter.UserId = undefined;
    this.getCollection(Constants.DefaultPageNumber);
  }

  handleUserChanged(user: UserBase): void {
    if (user) {
      this.financialTransactionFilter.UserId = user.UserId;
    } else {
      this.financialTransactionFilter.UserId = undefined;
    }

    this.getCollection(Constants.DefaultPageNumber);
  }

  handleModuleChanged(module: ModuleBase): void {
    if (module) {
      this.financialTransactionFilter.ModuleId = module.ModuleId;
    } else {
      this.financialTransactionFilter.ModuleId = undefined;
    }

    this.getCollection(Constants.DefaultPageNumber);
  }

  handleBooleanToggleFilterChanged(newValue: boolean): void {
    this.financialTransactionFilter.IsCreditUpgrade = newValue;
    if (newValue) {
      this.financialTransactionFilter.ModuleId = null;
    }
    this.getCollection();
  }

  openExportModal(): void {
    const modalRef = this.modalService.open(FinancialTransactionsExportComponent, {windowClass: 'add-edit-modal'});
    modalRef.componentInstance.companyId = this.companyId;
  }

  getCollection(pageNumber: number = 1): void {
    if (pageNumber) {
      this.pageConfiguration.PageNumber = pageNumber;
    }

    this.financialTransactionService.getCollectionPaginated<FinancialTransaction>('/financial-transaction', this.pageConfiguration, this.financialTransactionFilter, (collection: FinancialTransaction[]): void => {
      this.collection = collection;
    });
  }

  close(): void {
    this.router.navigate(['/companies']);
  }
}
